// TelcoTrack — karta srovnání prostavěnosti a plánovaného příjmu.

import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ThemeService } from '../../../core/services/theme.service';
import { buildKpiFractionPieOption } from '../../utils/kpi-fraction-pie.echarts';
import type { FinanceProgressComparisonModel } from '../../utils/finance-progress-comparison.util';
import { prefersReducedMotion, STAT_MOTION_MS } from '../../utils/stat-motion';

const CATEGORY_COLOR: Record<string, { light: string; dark: string }> = {
  vykop: { light: '#2563eb', dark: '#60a5fa' },
  montaz: { light: '#059669', dark: '#34d399' },
  optika: { light: '#d97706', dark: '#fbbf24' },
  ostatni: { light: '#7c3aed', dark: '#c4b5fd' },
};

@Component({
  selector: 'app-finance-progress-comparison',
  templateUrl: './finance-progress-comparison.component.html',
  styleUrls: ['./finance-progress-comparison.component.scss'],
})
export class FinanceProgressComparisonComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private physicalChart: ECharts | null = null;
  private incomeChart: ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private reducedMotion = prefersReducedMotion();
  private chartThemeIsDark = false;

  /** 0 until the first frame, then 1 — bar width transitions over STAT_MOTION_MS. */
  barScale = this.reducedMotion ? 1 : 0;

  @Input() model: FinanceProgressComparisonModel | null = null;
  @Input() progressError: string | null = null;

  @ViewChild('physicalHost') physicalHost?: ElementRef<HTMLDivElement>;
  @ViewChild('incomeHost') incomeHost?: ElementRef<HTMLDivElement>;
  @ViewChild('chartRow') chartRow?: ElementRef<HTMLDivElement>;

  constructor(private readonly themeService: ThemeService) {
    this.themeService.currentTheme$.pipe(takeUntil(this.destroy$)).subscribe((theme) => {
      this.chartThemeIsDark = theme === 'dark';
      setTimeout(() => this.syncCharts(), 0);
    });
  }

  ngAfterViewInit(): void {
    this.replayBars();
    setTimeout(() => this.syncCharts(), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && !changes['model'].firstChange) {
      this.replayBars();
    }
    setTimeout(() => this.syncCharts(), 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.physicalChart?.dispose();
    this.incomeChart?.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get barTransition(): string {
    if (this.reducedMotion) return 'none';
    return `width ${STAT_MOTION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
  }

  get showPhysicalLegend(): boolean {
    return !!this.model?.categories.some((cat) => cat.physicalLabel != null);
  }

  get showIncomeLegend(): boolean {
    return !!this.model?.categories.some((cat) => cat.incomeLabel != null);
  }

  categoryColor(id: string): string {
    const palette = CATEGORY_COLOR[id] ?? CATEGORY_COLOR['ostatni'];
    return this.chartThemeIsDark ? palette.dark : palette.light;
  }

  barWidth(percent: number): number {
    return Math.max(0, percent) * this.barScale;
  }

  trackCategory(_index: number, cat: { id: string }): string {
    return cat.id;
  }

  trackSummary(_index: number, line: { id: string }): string {
    return line.id;
  }

  private replayBars(): void {
    if (this.reducedMotion || prefersReducedMotion()) {
      this.reducedMotion = true;
      this.barScale = 1;
      return;
    }
    this.barScale = 0;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.barScale = 1;
      });
    });
  }

  private syncCharts(): void {
    this.physicalChart = this.syncDonut(
      this.physicalChart,
      this.physicalHost?.nativeElement,
      this.model?.physical?.percent ?? null,
      this.chartThemeIsDark ? '#2dd4bf' : '#0d9488',
    );
    this.incomeChart = this.syncDonut(
      this.incomeChart,
      this.incomeHost?.nativeElement,
      this.model?.income?.percent ?? null,
      this.chartThemeIsDark ? '#e2e8f0' : '#1e293b',
    );
    this.observeResize();
  }

  private syncDonut(
    chart: ECharts | null,
    el: HTMLDivElement | undefined,
    percent: number | null,
    accent: string,
  ): ECharts | null {
    if (!el || percent == null) {
      chart?.dispose();
      return null;
    }
    const option = buildKpiFractionPieOption({
      completed: percent,
      total: 100,
      accentColor: accent,
      dark: this.chartThemeIsDark,
      reduceMotion: prefersReducedMotion(),
    });
    if (!option) {
      chart?.dispose();
      return null;
    }
    const instance = chart ?? echarts.init(el, undefined, { renderer: 'canvas' });
    instance.setOption(option, { notMerge: true, lazyUpdate: false });
    instance.resize();
    return instance;
  }

  private observeResize(): void {
    const el = this.chartRow?.nativeElement;
    if (!el || this.resizeObserver || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      this.physicalChart?.resize();
      this.incomeChart?.resize();
    });
    this.resizeObserver.observe(el);
  }
}
